package pl.nickers.match3

import com.russhwolf.settings.Settings
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.nickers.match3.ecs.FamiliarType
import pl.nickers.match3.ecs.PersistableComponent
import pl.nickers.match3.ecs.World

@Serializable
data class ContinueContext(
    val gameMode: String,           // "arcade" or "journey"
    val journeyLevelId: Int? = null,
)

@Serializable
data class PersistedEntity(val components: List<PersistableComponent>)

/**
 * Save-format version. Bumped whenever the schema of [WorldSnapshot] or the
 * persisted component model changes in a way that pre-existing saves cannot
 * be safely restored from. Saves without a [WorldSnapshot.version] tag — or
 * with a version older than [SAVE_FORMAT_VERSION] — are rejected on load.
 *
 * v2: piece-set ids stamped on the board entity; level definitions use
 *     `pieceId` strings instead of integer jelly-type ids.
 * v3: `Goal.jellyType: Int` replaced with `Goal.pieceId: String` — pre-v3
 *     saved goals can't be migrated automatically.
 * v4: `BoardStateComponent.gridSize: Int` replaced with separate
 *     `width`/`height` fields for rectangular boards — pre-v4 saves are rejected.
 * v5: jelly piece ids reskinned to magical-ingredient names (`jelly_red` →
 *     `ember_seed`, etc.); pre-v5 saved boards carry obsolete piece ids and
 *     are rejected.
 * v6: ice blocker reskinned as Crystallized Amber (`ice_cube` →
 *     `crystallized_amber`); pre-v6 saved boards carry the obsolete piece id
 *     and are rejected.
 */
const val SAVE_FORMAT_VERSION = 6

/**
 * A fully serializable snapshot of the ECS world. Contains every entity whose
 * component set has at least one [PersistableComponent]; purely transient
 * entities (e.g. those carrying only animation-state components) are excluded.
 *
 * On restore, entities are recreated with exactly the persisted components.
 * Transient components are absent and will either be added by the first
 * processing frame or remain absent (which is the correct initial state).
 *
 * The [version] field is set to [SAVE_FORMAT_VERSION] when the snapshot is taken;
 * saves with a missing or older version are rejected by [GameStorage.loadSnapshot].
 */
@Serializable
data class WorldSnapshot(
    val entities: List<PersistedEntity>,
    val version: Int = 0,
)

fun World.snapshot(): WorldSnapshot {
    val entities = getAllEntityIds().mapNotNull { entityId ->
        val persistable = getAllComponentsForEntity(entityId).filterIsInstance<PersistableComponent>()
        if (persistable.isEmpty()) null else PersistedEntity(persistable)
    }
    return WorldSnapshot(entities = entities, version = SAVE_FORMAT_VERSION)
}

fun World.restoreFrom(snapshot: WorldSnapshot) {
    snapshot.entities.forEach { persistedEntity ->
        val entityId = createEntity()
        persistedEntity.components.forEach { component ->
            addComponent(entityId, component)
        }
    }
}

class GameStorage(private val settings: Settings = createDefaultSettings()) {

    var highScore: Int
        get() = settings.getInt(KEY_HIGH_SCORE, 0)
        set(value) { settings.putInt(KEY_HIGH_SCORE, value) }

    /** The Familiar the player has chosen to bring into levels. Defaults to the Black Cat. */
    var selectedFamiliar: FamiliarType
        get() = settings.getStringOrNull(KEY_SELECTED_FAMILIAR)
            ?.let { runCatching { FamiliarType.valueOf(it) }.getOrNull() }
            ?: FamiliarType.BLACK_CAT
        set(value) { settings.putString(KEY_SELECTED_FAMILIAR, value.name) }

    /** The highest level the player has unlocked; level 1 is always available. */
    var highestUnlockedLevel: Int
        get() = settings.getInt(KEY_HIGHEST_UNLOCKED, 1)
        set(value) { settings.putInt(KEY_HIGHEST_UNLOCKED, value) }

    /**
     * Whether SFX/music playback is enabled. Defaults to true. Settings-screen toggle
     * (issue #110); the key name is shared with issue #109, which wires the actual
     * muting behaviour into [AudioPlayer] — this flag only persists the player's choice.
     */
    var soundEnabled: Boolean
        get() = settings.getBoolean(KEY_SOUND_ENABLED, true)
        set(value) { settings.putBoolean(KEY_SOUND_ENABLED, value) }

    fun saveSnapshot(snapshot: WorldSnapshot) {
        settings.putString(KEY_SAVED_GAME, Json.encodeToString(snapshot))
    }

    fun loadSnapshot(): WorldSnapshot? {
        val json = settings.getStringOrNull(KEY_SAVED_GAME) ?: return null
        val snapshot = try {
            Json.decodeFromString<WorldSnapshot>(json)
        } catch (_: Exception) {
            return null
        }
        // Reject saves taken before the current format version. Older snapshots
        // predate the data-driven piece-set refactor and cannot be safely restored.
        if (snapshot.version < SAVE_FORMAT_VERSION) return null
        return snapshot
    }

    fun hasSavedGame(): Boolean = settings.getStringOrNull(KEY_SAVED_GAME) != null

    fun clearSavedGame() {
        settings.remove(KEY_SAVED_GAME)
    }

    var continueContext: ContinueContext?
        get() = settings.getStringOrNull(KEY_CONTINUE_CONTEXT)?.let {
            try { Json.decodeFromString(it) } catch (_: Exception) { null }
        }
        set(value) {
            if (value != null)
                settings.putString(KEY_CONTINUE_CONTEXT, Json.encodeToString(value))
            else
                settings.remove(KEY_CONTINUE_CONTEXT)
        }

    var userId: String
        get() {
            val existing = settings.getStringOrNull(KEY_USER_ID)
            if (existing != null) return existing
            val generated = generateUserId()
            settings.putString(KEY_USER_ID, generated)
            return generated
        }
        set(value) { settings.putString(KEY_USER_ID, value) }

    var displayName: String
        get() = settings.getString(KEY_DISPLAY_NAME, "Player")
        set(value) { settings.putString(KEY_DISPLAY_NAME, value) }

    /**
     * The player's gem balance. **Only [WalletService] may write this** — every
     * earn and spend in the game goes through that single choke point (E18/F18.1).
     */
    var gems: Int
        get() = settings.getInt(KEY_GEMS, 0)
        set(value) { settings.putInt(KEY_GEMS, value) }

    var livesCount: Int
        get() = settings.getInt(KEY_LIVES_COUNT, MAX_LIVES)
        set(value) { settings.putInt(KEY_LIVES_COUNT, value.coerceIn(0, MAX_LIVES)) }

    var livesLastRefillTimestamp: Long
        get() = settings.getLong(KEY_LIVES_LAST_REFILL_TS, 0L)
        set(value) { settings.putLong(KEY_LIVES_LAST_REFILL_TS, value) }

    /** True once the one-time starter grant has been paid; keeps it idempotent across restarts. */
    var starterGrantPaid: Boolean
        get() = settings.getBoolean(KEY_STARTER_GRANT_PAID, false)
        set(value) { settings.putBoolean(KEY_STARTER_GRANT_PAID, value) }

    /**
     * Journey levels whose first-win gem bonus has already been paid out.
     *
     * ASSUMPTION: stored as a comma-separated id list rather than JSON —
     * multiplatform-settings has no set primitive, and the list stays small
     * (one entry per journey level). Unparsable entries are dropped rather than
     * failing the load, so a corrupted value costs at most a re-payout.
     */
    var gemAwardedLevelIds: Set<Int>
        get() = settings.getStringOrNull(KEY_GEM_AWARDED_LEVELS)
            ?.split(',')
            ?.mapNotNull { it.trim().toIntOrNull() }
            ?.toSet()
            ?: emptySet()
        set(value) { settings.putString(KEY_GEM_AWARDED_LEVELS, value.sorted().joinToString(",")) }

    var lifetimeLevelsCompleted: Int
        get() = settings.getInt(KEY_LIFETIME_LEVELS_COMPLETED, 0)
        set(value) { settings.putInt(KEY_LIFETIME_LEVELS_COMPLETED, value) }

    var lifetimePowerUpsBomb: Int
        get() = settings.getInt(KEY_LIFETIME_PU_BOMB, 0)
        set(value) { settings.putInt(KEY_LIFETIME_PU_BOMB, value) }

    var lifetimePowerUpsRowBomb: Int
        get() = settings.getInt(KEY_LIFETIME_PU_ROW_BOMB, 0)
        set(value) { settings.putInt(KEY_LIFETIME_PU_ROW_BOMB, value) }

    var lifetimePowerUpsTargetedRemove: Int
        get() = settings.getInt(KEY_LIFETIME_PU_TARGETED_REMOVE, 0)
        set(value) { settings.putInt(KEY_LIFETIME_PU_TARGETED_REMOVE, value) }

    /** Best star rating (0–3) earned for journey level [levelId]. 0 = unplayed. */
    fun starsForLevel(levelId: Int): Int = settings.getInt("${KEY_STARS_PREFIX}$levelId", 0)

    /** Stores the best star rating for journey level [levelId]. */
    fun setStarsForLevel(levelId: Int, stars: Int) {
        settings.putInt("${KEY_STARS_PREFIX}$levelId", stars)
    }

    /** Removes every persisted per-level star rating (used by journey progress reset). */
    fun clearAllStars() {
        settings.keys.filter { it.startsWith(KEY_STARS_PREFIX) }.forEach { settings.remove(it) }
    }

    fun loadProfile(): UserProfile = UserProfile(
        userId = userId,
        displayName = displayName,
        highScore = highScore,
        highestUnlockedLevel = highestUnlockedLevel,
        lifetimeLevelsCompleted = lifetimeLevelsCompleted,
        gems = gems,
        lifetimePowerUpsBomb = lifetimePowerUpsBomb,
        lifetimePowerUpsRowBomb = lifetimePowerUpsRowBomb,
        lifetimePowerUpsTargetedRemove = lifetimePowerUpsTargetedRemove,
    )

    companion object {
        private const val KEY_HIGH_SCORE = "high_score"
        private const val KEY_SELECTED_FAMILIAR = "selected_familiar"
        private const val KEY_SAVED_GAME = "saved_game"
        private const val KEY_HIGHEST_UNLOCKED = "highest_unlocked_level"
        private const val KEY_SOUND_ENABLED = "sound_enabled"
        private const val KEY_CONTINUE_CONTEXT = "continue_context"
        private const val KEY_USER_ID = "user_id"
        private const val KEY_DISPLAY_NAME = "display_name"
        private const val KEY_GEMS = "gems"
        private const val KEY_LIVES_COUNT = "lives_count"
        private const val KEY_LIVES_LAST_REFILL_TS = "lives_last_refill_ts"
        private const val KEY_STARTER_GRANT_PAID = "economy_starter_grant_paid"
        private const val KEY_GEM_AWARDED_LEVELS = "economy_first_win_paid_levels"
        private const val KEY_LIFETIME_LEVELS_COMPLETED = "lifetime_levels_completed"
        private const val KEY_LIFETIME_PU_BOMB = "lifetime_pu_bomb"
        private const val KEY_LIFETIME_PU_ROW_BOMB = "lifetime_pu_row_bomb"
        private const val KEY_LIFETIME_PU_TARGETED_REMOVE = "lifetime_pu_targeted_remove"
        private const val KEY_STARS_PREFIX = "stars_"
    }
}

private fun generateUserId(): String {
    val hex = "0123456789abcdef"
    val r = kotlin.random.Random.Default
    return buildString {
        repeat(32) { i ->
            if (i == 8 || i == 12 || i == 16 || i == 20) append('-')
            append(hex[r.nextInt(16)])
        }
    }
}
